import React, { useState, useEffect } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import { get, map } from 'lodash';
import { useIntl } from 'react-intl';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Collapse from '@material-ui/core/Collapse';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

import {
  AlertDialog, LoadingDialog,
 } from '../../../../components';
import {
  resetDeleteApplicationStatus,
  deleteApplication,
} from '../../../../actions/application';
import {
  getDeleteApplicationStatus,
  getErrors,
} from '../../../../reducers/application';
import {
  FAIL,
  SUCCESS,
  ROLE_DEVELOPER,
  APPLICATION_STATUS_EDIT_PENDING_APPROVAL,
} from '../../../../constants';
import {
  getUserRole,
  hasOrgBoundRole,
  hasPublisherRole,
} from '../../../../utils';
import { getI18n } from '../../../../utils/intl';
import styles from './styles';

export const processErrors = (arrErrors, defaultMessage) => {
  let errorMessage = defaultMessage;
  if (arrErrors.length > 0) {
    errorMessage = map(arrErrors, (validationError) => validationError.error).join('\n');
  }
  return errorMessage;
};

export const ActionsRaw = (props) => {
  const {
    applicationDetails,
    isEditAppDisabled,
    isEditAppKeysDisabled,
    setNotificationStatus,
    setNotificationMessage,
    deleteAppStatus,
    isMultiKeySupport,
    referrerUrl,
    errors,
    history,
    user,
    classes,
  } = props;
  const [open, setOpen] = useState(false);
  const handleClick = () => { setOpen(!open); };
  const handleClickAway = () => { setOpen(false); };
  const appUuid = get(applicationDetails, 'uuid');
  const intl = getI18n(useIntl());

  // canEdit
  const status = get(applicationDetails, 'status');
  const editPendingApproval = status === APPLICATION_STATUS_EDIT_PENDING_APPROVAL;
  const [showDeleteAppDialog, setShowDeleteAppDialog] = useState(false);
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const openDeleteAppDialog = () => setShowDeleteAppDialog(true);
  const closeDeleteAppDialog = () => setShowDeleteAppDialog(false);
  const openLoadingDialog = () => setShowLoadingDialog(true);
  const closeLoadingDialog = () => setShowLoadingDialog(false);

  const deleteApp = async () => {
    await props.deleteApplication(applicationDetails);
    closeLoadingDialog();
  };

  const actions = [
    {
      key: '1',
      value: 'Edit Application',
      disabled: isEditAppDisabled,
      onClick: () => {
        props.resetDeleteApplicationStatus();
        history.push(`/publish/applications/edit/${appUuid}`);
      },
      visible: true,
    },
    {
      key: '2',
      value: 'Edit API Assignment',
      disabled: isEditAppDisabled,
      onClick: () => {
        props.resetDeleteApplicationStatus();
        history.push(`/publish/applications/edit/${appUuid}/api-management`);
      },
      visible: true,
    },
    {
      key: '3',
      value: 'Edit Keys',
      disabled: isEditAppKeysDisabled,
      onClick: () => {
        props.resetDeleteApplicationStatus();
        history.push(`/publish/applications/edit/${appUuid}/keys`);
      },
      visible: (isMultiKeySupport && hasPublisherRole(user)),
    },
    {
      key: '4',
      value: 'Delete Application',
      disabled: editPendingApproval,
      onClick: () => openDeleteAppDialog(),
      visible: getUserRole(user) !== ROLE_DEVELOPER,
    },
  ];

  const isDevUrl = referrerUrl.indexOf('/dev/') !== (-1);
  useEffect(() => {
    if (deleteAppStatus === SUCCESS) {
      let redirectUrl = '';
      if (isDevUrl) {
        redirectUrl = '/dev/apps';
      } else {
        redirectUrl = hasOrgBoundRole(user) ? '/admin/app/applications' : '/admin/applications';
      }
      window.location.href = redirectUrl;
    } else if (deleteAppStatus === FAIL) {
      const defaultErrorMessage = 'There was an error deleting this application.';
      const errorMessage = processErrors(errors, defaultErrorMessage);
      setNotificationStatus('error');
      setNotificationMessage(errorMessage);
    }
  }, [deleteAppStatus]);

  return (
    <div
      className={classes.actionsContainer}
      id="app-details-actions-container" data-layer7-test="app-details-actions-container"
    >
      <AlertDialog
        isOpen={showDeleteAppDialog}
        title={intl.getI18nMessage('label.application.delete')}
        description={intl.getI18nMessage('label.application.delete.confirmation.text')}
        submitText={intl.getI18nMessage('label.ok.button')}
        cancelText={intl.getI18nMessage('label.cancel.button')}
        onClose={closeDeleteAppDialog}
        onSubmit={() => {
          closeDeleteAppDialog();
          openLoadingDialog();
          deleteApp();
        }}
        onCancel={closeDeleteAppDialog}
        dialogId="app-delete-dialog"
      />
      <LoadingDialog
        isOpen={showLoadingDialog}
        title={intl.getI18nMessage('label.application.deleting')}
        description={intl.getI18nMessage('label.application.deleting.status.text')}
        dialogId="deleting-app-dialog"
      />
      <ClickAwayListener onClickAway={handleClickAway}>
        <List component="nav">
          <ListItem classes={{ root: classes.listButton }} button onClick={handleClick}>
            <ListItemText primary={'Actions'} />
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse className={classes.collapseContainer} in={open} unmountOnExit>
            {actions.filter(({ visible }) => visible).map((obj) => (
              <List key={obj.key} component="div" disablePadding>
                <ListItem
                  button
                  disabled={obj.disabled}
                  onClick={obj.onClick}
                  className={classes.listItem}
                  data-layer7-test={obj.value}
                >
                  <ListItemText primary={obj.value} />
                </ListItem>
              </List>
            ))}
          </Collapse>
        </List>
      </ClickAwayListener>
    </div>
  );
};

const mapStateToProps = (state) => ({
  deleteAppStatus: getDeleteApplicationStatus(state),
  errors: getErrors(state),
});

const mapDispatchToProps = {
  resetDeleteApplicationStatus,
  deleteApplication,
};

ActionsRaw.propTypes = {
  applicationDetails: PropTypes.shape({}),
  isEditAppDisabled: PropTypes.bool,
  isEditAppKeysDisabled: PropTypes.bool,
  setNotificationMessage: PropTypes.func,
  setNotificationStatus: PropTypes.func,
  deleteAppStatus: PropTypes.string,
  isMultiKeySupport: PropTypes.bool,
  referrerUrl: PropTypes.string,
  user: PropTypes.shape({}),
  errors: PropTypes.arrayOf(PropTypes.shape({})),
  history: PropTypes.shape({}),
  classes: PropTypes.shape({}),
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ActionsRaw);



// WEBPACK FOOTER //
// ./src/pages/Application/Details/Actions/index.js